// LayerSheet — base-map picker + grouped overlay toggles.
//
// Renders entirely from the LAYER_CATALOG so adding a layer in the catalog
// gives it a UI row automatically. Mutual-exclusion (heavy-overlay group)
// is enforced in the uiStore; here we only surface the relationship to
// the user with a small "Replaces: X" hint and a subtle group divider.

import { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FUEL_LEGEND_SVGS } from '../map/fuelIcons';
import { SKY_LEGEND_SVGS } from '../map/skyCoverIcons';
import { WIND_LEGEND_SVGS } from '../map/windBarbs';
import { useUiStore } from '../stores/uiStore';
import { useLayerVisibilityStore } from '../stores/layerVisibilityStore';
import { useLayerStatusStore, formatRelative, LayerKey } from '../stores/layerStatusStore';
import {
  LAYER_CATALOG,
  LayerDef,
  LayerId,
  getLayersInGroup,
} from '../map/layerCatalog';
// HI-1 step 1 (2026-05-23): LayerSheet used to hard-import 5 named
// refresh wrappers from MapView (fires, sua, tfr, gfaPublic, cwaPublic).
// Now it iterates the RefreshableOverlayRegistry, which MapView's
// public wrappers register on module load. Adding a new refreshable
// overlay is one registry call — no edits to LayerSheet needed.
//
// We still import MapView (as a side-effect: the file's top-level
// `registerRefreshableOverlay(...)` calls execute on import) so the
// registry is populated before this component renders.
import '../map/MapView';
import { refreshAllRegisteredOverlays } from '../map/layerVisibilityRegistry';
import { useAgolAuthStore } from '../stores/agolAuthStore';
import type { BaseImageryId, ChartOverlayId } from '../map/sources';

const IMAGERY: Array<{ id: BaseImageryId; label: string; sub: string }> = [
  { id: 'basic',     label: 'Basic',     sub: 'Light streets' },
  { id: 'street',    label: 'Street',    sub: 'OSM full' },
  { id: 'satellite', label: 'Satellite', sub: 'Esri imagery' },
  { id: 'topo',      label: 'Topo',      sub: 'USGS terrain' },
];

const CHARTS: Array<{ id: ChartOverlayId; label: string; sub: string }> = [
  { id: 'none',      label: 'None',      sub: 'Imagery only' },
  { id: 'sectional', label: 'Sectional', sub: 'VFR' },
  { id: 'ifr-low',   label: 'IFR Low',   sub: 'Enroute' },
  { id: 'ifr-high',  label: 'IFR High',  sub: 'Enroute' },
  { id: 'helo',      label: 'Helicopter',sub: 'HRC' },
];

export default function LayerSheet({ onClose }: { onClose: () => void }) {
  const baseImagery = useUiStore((s) => s.baseImagery);
  const setBaseImagery = useUiStore((s) => s.setBaseImagery);
  const chartOverlay = useUiStore((s) => s.chartOverlay);
  const setChartOverlay = useUiStore((s) => s.setChartOverlay);
  const chartOpacity = useUiStore((s) => s.chartOpacity);
  const setChartOpacity = useUiStore((s) => s.setChartOpacity);
  const layers = useLayerVisibilityStore((s) => s.layers);
  const toggleLayer = useLayerVisibilityStore((s) => s.toggleLayer);

  // Force a re-render every 30s so "2m ago" stays accurate.
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 30 * 1000);
    return () => clearInterval(t);
  }, []);

  const refreshAll = async () => {
    // HI-1: iterate the registry instead of a hardcoded list. Today
    // MapView registers fires/tfr/sua/gfa/cwa/flightcat/pireps/classBCD/
    // airways at module load; the manual button refreshes every registered
    // overlay in parallel. Behavior superset of the prior hardcoded
    // five: includes flightcat/pireps/classBCD/airways too, but each
    // refresh internally checks if the overlay should auto-load, so a
    // refresh of an inactive overlay is cheap.
    await refreshAllRegisteredOverlays();
  };

  // The hazard-bar (`wxhazards` master toggle) replaces the per-family
  // SIGMET / G-AIRMET / CWA rows that used to live in this list. Hide
  // those + the legacy 'airmet' from the LayerSheet — their state is
  // now driven by chips inside the bar itself. The 8 GFA hazard sub-
  // layers are likewise driven by the AIRMET chip's expanded sub-row.
  const HIDDEN_FROM_LAYERSHEET = new Set(['airmet', 'sigmet', 'cwa']);
  // FireGuard + NIROPS toggles are wired into layerCatalog (id, label,
  // status-key, AGOL-auth gating) but their actual GeoJSON fetch +
  // layer-source plumbing isn't built yet — we don't have AGOL
  // permissions for those NIFC feature services. ME-14 (audit synthesis
  // 2026-05-22): keep the catalog + status-store stubs in place so the
  // wiring is ready when access is granted, but hide the UI rows so
  // the user doesn't see toggles that silently no-op. Restore by
  // dropping the ids from this set once the layer modules ship.
  const HIDDEN_HEAVY = new Set(['fireguard', 'nirops']);
  const heavyLayers = getLayersInGroup('heavy-overlay')
    .filter((l) => !HIDDEN_HEAVY.has(l.id));
  const weatherLayers = getLayersInGroup('weather')
    .filter((l) => !HIDDEN_FROM_LAYERSHEET.has(l.id));
  const opsLayers = getLayersInGroup('ops');

  const agolReady = useAgolAuthStore((s) => s.ready);
  const agolConfigured = useAgolAuthStore((s) => s.configured);
  const agolSignedIn = useAgolAuthStore((s) => s.signedIn);
  const nav = useNavigate();

  /**
   * A layer is "locked" when it requires auth but no usable session
   * exists yet. We surface a 'Sign in' affordance instead of a normal
   * toggle for these rows.
   */
  const isLocked = (def: LayerDef) =>
    def.auth === 'agol-nifc' && (!agolConfigured || (agolReady && !agolSignedIn));

  return (
    // Anchored top-right of the map. On desktop the topbar's search
    // input also lives in the upper-right, so we drop the sheet a few
    // rows lower (top-20) instead of stacking it directly below the
    // search field. Plan panel and Layers are mutually exclusive in
    // MapPage, so they can't fight for the same screen real estate.
    //
    // Wrapped in a full-screen transparent backdrop so tapping anywhere
    // off-sheet dismisses it — the original UX forced the user to
    // scroll back to the corner button on mobile, which boss flagged.
    // The backdrop sits BELOW the top map bar (z-30) and above the map
    // canvas; the sheet itself stops propagation so taps on it don't
    // close.
    <div
      className="absolute inset-0 z-30"
      onClick={onClose}
      role="presentation"
    >
    <div
      className="absolute top-16 right-3 lg:top-20 w-72 max-h-[80vh] overflow-y-auto scroll-touch bg-cockpit-800/95 backdrop-blur border border-cockpit-600 rounded-xl shadow-rail p-3"
      style={{ marginTop: 'var(--safe-top)' }}
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-label="Layers"
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-cockpit-100 text-sm">Layers</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={refreshAll}
            className="text-cockpit-300 hover:text-ember-400"
            title="Refresh data layers"
            aria-label="Refresh layers"
          >
            ↻
          </button>
          <button onClick={onClose} className="text-cockpit-300 text-xs">Close</button>
        </div>
      </div>

      <div className="text-[10px] tracking-widest text-cockpit-300 mb-1">IMAGERY</div>
      <div className="grid grid-cols-3 gap-1.5 mb-3">
        {IMAGERY.map((b) => (
          <button
            key={b.id}
            onClick={() => setBaseImagery(b.id)}
            className={[
              'rounded-md border px-1.5 py-1.5 text-left',
              baseImagery === b.id
                ? 'bg-ember-600/25 border-ember-500'
                : 'bg-cockpit-700 border-cockpit-600 hover:bg-cockpit-600',
            ].join(' ')}
          >
            <div className="text-[11px] text-cockpit-100 leading-tight">{b.label}</div>
            <div className="text-[9px] text-cockpit-300">{b.sub}</div>
          </button>
        ))}
      </div>

      <div className="text-[10px] tracking-widest text-cockpit-300 mb-1">CHART</div>
      <div className="grid grid-cols-3 gap-1.5 mb-3">
        {CHARTS.map((c) => (
          <button
            key={c.id}
            onClick={() => setChartOverlay(c.id)}
            className={[
              'rounded-md border px-1.5 py-1.5 text-left',
              chartOverlay === c.id
                ? 'bg-ember-600/25 border-ember-500'
                : 'bg-cockpit-700 border-cockpit-600 hover:bg-cockpit-600',
            ].join(' ')}
          >
            <div className="text-[11px] text-cockpit-100 leading-tight">{c.label}</div>
            <div className="text-[9px] text-cockpit-300">{c.sub}</div>
          </button>
        ))}
      </div>

      {/* Chart opacity — only meaningful when a chart overlay is
          actually selected. Hidden when chart=none so the row doesn't
          falsely imply something is going on. */}
      {chartOverlay !== 'none' && (
        <div className="mb-3">
          <div className="flex items-baseline justify-between mb-1">
            <span className="text-[10px] tracking-widest text-cockpit-300">CHART OPACITY</span>
            <span className="font-mono text-[10px] text-cockpit-100">
              {Math.round(chartOpacity * 100)}%
            </span>
          </div>
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={chartOpacity}
            onChange={(e) => setChartOpacity(parseFloat(e.target.value))}
            aria-label="Chart opacity"
            className="w-full accent-ember-500"
          />
          <div className="flex justify-between text-[9px] text-cockpit-400">
            <span>Imagery</span>
            <span>Chart</span>
          </div>
        </div>
      )}

      {/* Heavy overlay group — radio-style behavior. */}
      <GroupHeader title="OVERLAY" hint="Pick one — heavy data, exclusive" />
      <div className="space-y-0.5 mb-3">
        {heavyLayers.map((def) => (
          <Fragment key={def.id}>
            <LayerRow
              def={def}
              checked={layers[def.id]}
              onToggle={() => toggleLayer(def.id)}
              siblings={heavyLayers.filter((s) => s.id !== def.id && layers[s.id])}
              locked={isLocked(def)}
              agolConfigured={agolConfigured}
              onUnlock={() => { onClose(); nav('/settings'); }}
            />
            {/* Radar product picker (Composite/Classic/Low Tilt) — mirrors
                ForeFlight's three radar layers. Renders directly under the
                Radar row when the layer is on. (Boss 2026-05-21 19:42 PDT.)
                The 'nexrad' layer lives in heavy-overlay, NOT 'weather' —
                first attempt wired this into the weather loop, hence the
                picker never appeared. */}
            {def.id === 'nexrad' && layers.nexrad && <RadarVariantPicker />}
          </Fragment>
        ))}
      </div>

      <GroupHeader title="WEATHER" />
      <div className="space-y-0.5 mb-3">
        {weatherLayers.map((def) => (
          <LayerRow
            key={def.id}
            def={def}
            checked={layers[def.id]}
            onToggle={() => toggleLayer(def.id)}
            locked={isLocked(def)}
            agolConfigured={agolConfigured}
            onUnlock={() => { onClose(); nav('/settings'); }}
          />
        ))}
      </div>

      <GroupHeader title="OPS" />
      <div className="space-y-0.5">
        {opsLayers.map((def) => (
          <LayerRow
            key={def.id}
            def={def}
            checked={layers[def.id]}
            onToggle={() => toggleLayer(def.id)}
            locked={isLocked(def)}
            agolConfigured={agolConfigured}
            onUnlock={() => { onClose(); nav('/settings'); }}
          />
        ))}
      </div>

      <div className="mt-3 px-1">
        <ActiveLayerLegend layers={layers} />
        <SuaLegend />
      </div>

      <p className="mt-3 text-[10px] leading-snug text-cockpit-300 px-1">
        Supplemental only — verify all data with current FAA sources before flight.
      </p>
    </div>
    </div>
  );
}

/** Contextual legend at the bottom of the LayerSheet. Only shows the
 *  legend block for layers that are CURRENTLY ON. Boss feedback
 *  2026-05-10: render the ACTUAL ICONS (not just text descriptions)
 *  so a quick glance at the legend matches what's drawn on the map.
 *  Each icon-bearing layer block uses inline-SVG sprite + short label;
 *  layers without icons (NEXRAD, satellite, surface analysis) keep a
 *  short text caption. */
function ActiveLayerLegend({ layers }: { layers: Record<string, boolean> }) {
  const blocks: React.ReactNode[] = [];

  if (layers.flightcat) {
    blocks.push(
      <LegendBlock key="flightcat" title="Flight Category">
        <ColorSwatch color="#22c55e" label="VFR" />
        <ColorSwatch color="#3b82f6" label="MVFR" />
        <ColorSwatch color="#dc2626" label="IFR" />
        <ColorSwatch color="#a855f7" label="LIFR" />
        <ColorSwatch color="#6b7280" label="Unknown" />
      </LegendBlock>
    );
  }

  if (layers.ceilvis) {
    blocks.push(
      <LegendBlock key="ceilvis" title="Ceiling / Visibility / Sky Cover">
        <IconChip svg={SKY_LEGEND_SVGS.CLR} label="CLR" />
        <IconChip svg={SKY_LEGEND_SVGS.FEW} label="FEW" />
        <IconChip svg={SKY_LEGEND_SVGS.SCT} label="SCT" />
        <IconChip svg={SKY_LEGEND_SVGS.BKN} label="BKN" />
        <IconChip svg={SKY_LEGEND_SVGS.OVC} label="OVC" />
        <IconChip svg={SKY_LEGEND_SVGS.OVX} label="OVX" />
        <span className="text-[10px] text-cockpit-200 leading-tight w-full mt-1">
          Circle fill = cloud cover. Top number = ceiling in 100s of ft AGL
          (“—” when unrestricted). Bottom number = visibility in statute miles
          (“M¼” means &lt; ¼ sm).
        </span>
      </LegendBlock>
    );
  }

  if (layers.surfacewinds) {
    blocks.push(
      <LegendBlock key="sfcwind" title="Surface Winds">
        <IconChip svg={WIND_LEGEND_SVGS['5']}  label="5 kt" />
        <IconChip svg={WIND_LEGEND_SVGS['10']} label="10 kt" />
        <IconChip svg={WIND_LEGEND_SVGS['20']} label="20 kt" />
        <IconChip svg={WIND_LEGEND_SVGS['50']} label="≥50 kt" />
        <span className="text-[10px] text-cockpit-300 leading-tight w-full mt-1">
          Tail points the direction wind is FROM.
        </span>
      </LegendBlock>
    );
  }

  if (layers.dewspread) {
    blocks.push(
      <LegendBlock key="dewspread" title="Dewpoint Spread (T − Td)">
        <ColorSwatch color="#dc2626" label="&lt; 2°C" />
        <ColorSwatch color="#facc15" label="2–5°C" />
        <ColorSwatch color="#22c55e" label="≥ 5°C" />
        <ColorSwatch color="#6b7280" label="Unknown" />
      </LegendBlock>
    );
  }

  if (layers.temperature) {
    blocks.push(
      <LegendBlock key="temp" title="Temperature">
        <span className="text-[10px] text-cockpit-200 leading-tight">
          OAT in °C next to each station marker.
        </span>
      </LegendBlock>
    );
  }

  if (layers.pireps) {
    blocks.push(
      <LegendBlock key="pireps" title="PIREPs">
        <ColorSwatch color="#dc2626" label="Urgent / severe" />
        <ColorSwatch color="#fb923c" label="Turbulence" />
        <ColorSwatch color="#7dd3fc" label="Icing" />
        <ColorSwatch color="#facc15" label="Wind shear" />
        <ColorSwatch color="#a855f7" label="Wx (TS, etc.)" />
        <ColorSwatch color="#94a3b8" label="Cloud report" />
      </LegendBlock>
    );
  }

  if (layers.terrain) {
    blocks.push(
      <LegendBlock key="terrain" title="Terrain Advisor">
        <span className="text-[10px] text-cockpit-200 leading-tight">
          Hillshade rendering with a red/amber palette — high relief reads as red, slopes amber, low terrain near-transparent. Reference altitude comes from the right-hand vertical slider (GPS-locked by default); descending the slider increases hillshade contrast so terrain visually 'closes in'.
          For exact AGL terrain clearance read the AGL number on the slider or in the instrument bar.
        </span>
      </LegendBlock>
    );
  }

  if (layers.nexrad) {
    blocks.push(
      <LegendBlock key="nexrad" title="NEXRAD Reflectivity">
        <span className="text-[10px] text-cockpit-200 leading-tight">
          Greens → light precip; yellows → moderate; reds/purples → heavy / hail.
          Auto-refreshes every 5 minutes.
        </span>
      </LegendBlock>
    );
  }

  if (layers.sfcanalysis) {
    blocks.push(
      <LegendBlock key="sfcanalysis" title="Surface Analysis">
        <span className="text-[10px] text-cockpit-200 leading-tight">
          NWS WPC current chart — H/L pressure centers, fronts (red=warm, blue=cold,
          purple=occluded, dashed=stationary), isobars in millibars. Issued every 3 hours.
        </span>
      </LegendBlock>
    );
  }

  if (layers.satgeocolor || layers.satir) {
    blocks.push(
      <LegendBlock key="sat" title="Satellite">
        <span className="text-[10px] text-cockpit-200 leading-tight">
          GOES GeoColor — true-color daytime, synthetic IR nighttime. Color IR — cloud-top
          temperatures (red = cold tops = convection; blue/green = warm low cloud).
        </span>
      </LegendBlock>
    );
  }

  if (layers.fuel100ll || layers.fueljeta) {
    blocks.push(
      <LegendBlock key="fuel" title="Fuel">
        {layers.fuel100ll && <IconChip svg={FUEL_LEGEND_SVGS['100LL']} label="100LL avgas" />}
        {layers.fueljeta  && <IconChip svg={FUEL_LEGEND_SVGS['JetA']}  label="Jet-A" />}
        <span className="text-[10px] text-cockpit-300 leading-tight w-full mt-1">
          Colors match real fuel-dye color (NASR-truthy).
        </span>
      </LegendBlock>
    );
  }

  // Navaids/fixes legends removed 2026-05-21 — self-explanatory to
  // pilots; the layer-row swatch is enough.

  if (blocks.length === 0) return null;
  return (
    <div className="space-y-2 mb-3">
      <div className="text-[10px] tracking-widest text-cockpit-300">LEGEND</div>
      <div className="space-y-2">{blocks}</div>
    </div>
  );
}

function LegendBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-cockpit-700/30 border border-cockpit-700 rounded px-2 py-1.5">
      <div className="text-[10px] font-semibold text-cockpit-100 mb-1">{title}</div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">{children}</div>
    </div>
  );
}

/** Inline-SVG icon + short label — the boss-preferred legend format
 *  (matches what's drawn on the map exactly). */
function IconChip({ svg, label }: { svg: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] text-cockpit-200">
      <span
        className="inline-block w-5 h-5 shrink-0"
        // The inline SVG strings are app-controlled (built from our
        // own typed icon factories), so dangerouslySetInnerHTML is
        // safe here.
        dangerouslySetInnerHTML={{ __html: svg }}
        aria-hidden
      />
      {label}
    </span>
  );
}

function ColorSwatch({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] text-cockpit-200">
      <span
        className="inline-block h-2.5 w-2.5 rounded-full border border-cockpit-500"
        style={{ backgroundColor: color }}
        aria-hidden
      />
      <span dangerouslySetInnerHTML={{ __html: label }} />
    </span>
  );
}

function GroupHeader({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="flex items-baseline justify-between mb-1">
      <span className="text-[10px] tracking-widest text-cockpit-300">{title}</span>
      {hint && <span className="text-[9px] text-cockpit-400 italic">{hint}</span>}
    </div>
  );
}

function LayerRow({
  def,
  checked,
  onToggle,
  siblings,
  locked,
  agolConfigured,
  onUnlock,
}: {
  def: LayerDef;
  checked: boolean;
  onToggle: () => void;
  /** Other heavy-overlay layers currently active (used to show "Replaces: X"). */
  siblings?: LayerDef[];
  /** True when the layer needs auth but no session/config exists yet. */
  locked?: boolean;
  /** Whether the OAuth client ID is provisioned at all. */
  agolConfigured?: boolean;
  /** Tap target when locked — opens Settings to sign in. */
  onUnlock?: () => void;
}) {
  const status = useLayerStatusStore((s) =>
    def.statusKey ? s.byKey[def.statusKey as LayerKey] : undefined
  );

  // The heavy-overlay group's own section header at the top of the sheet
  // is the mutex cue — no per-row warning needed. (2026-05-21 boss.)

  // Locked rows aren't tappable as toggles — tapping routes to Settings.
  const handleClick = () => {
    if (locked) onUnlock?.();
    else onToggle();
  };

  const subline = locked
    ? (agolConfigured
        ? <span className="text-amber-400/80">Sign in to enable</span>
        : <span className="text-cockpit-400">NIFC AGOL not configured</span>)
    : status?.loading
      ? 'Loading…'
      : status?.error
        ? <span className="text-red-400">⚠ {status.error}</span>
        : status?.updatedAt != null
          ? <>
              {def.sub && <>{def.sub} · </>}
              {status.count != null && <>{status.count} · </>}
              {formatRelative(status.updatedAt)}
            </>
          : (def.sub ?? '');

  return (
    <button
      onClick={handleClick}
      className={[
        'w-full flex items-center gap-2 rounded-md px-1.5 py-1.5 text-left',
        locked ? 'opacity-60 hover:opacity-80' : 'hover:bg-cockpit-700',
      ].join(' ')}
    >
      <Swatch id={def.id} />
      <span className="flex-1 min-w-0">
        <div className="text-xs text-cockpit-100 leading-tight flex items-center gap-1">
          {def.label}
          {locked && <span aria-hidden className="text-[10px]">🔒</span>}
        </div>
        <div className="text-[10px] text-cockpit-300 leading-tight truncate">{subline}</div>
      </span>
      {locked
        ? <span className="text-[10px] text-cockpit-300">Settings→</span>
        : <Switch checked={checked} />}
    </button>
  );
}

function Swatch({ id }: { id: LayerId }) {
  // 14-16-px legend swatch. Distinct shape/color per layer so the
  // LayerSheet reads as a glanceable map legend instead of a list of
  // grey squares. The on-map appearance is mirrored where reasonable
  // so a pilot can map a layer name back to what they see.
  switch (id) {
    // ---- Heavy overlays (fire / SUA) ------------------------------
    case 'fires':
      return <Square className="bg-ember-500/30 border-ember-500" />;
    case 'fireguard':
      return <Square className="bg-rose-500/30 border-rose-500" />;
    case 'nirops':
      return <Hatched className="bg-amber-500/30 border-amber-500" hatching="orange" />;
    case 'sua':
      return (
        <div className="flex gap-px h-3 w-3">
          <span className="flex-1 bg-purple-500/50" />
          <span className="flex-1 bg-orange-500/50" />
          <span className="flex-1 bg-yellow-500/50" />
        </div>
      );
    case 'viirs':
      return <Dot className="bg-red-500" />;
    case 'tfr':
      return <Hatched className="bg-red-500/30 border-red-500" hatching="red" />;

    // ---- Weather (airmet / sigmet / cwa / hazards) ----------------
    case 'airmet':
      return <LetterChip letter="A" fg="text-blue-200" bg="bg-blue-500/20" border="border-blue-400" dashed />;
    case 'sigmet':
      return <LetterChip letter="S" fg="text-red-100" bg="bg-red-500/40" border="border-red-400" />;
    case 'cwa':
      return <LetterChip letter="C" fg="text-amber-100" bg="bg-amber-500/30" border="border-amber-400" />;
    case 'wxhazards':
      // Master toggle for the AIRMET/SIGMET/CWA bar — use a stacked
      // tri-color sliver to signal "all hazards".
      return (
        <div className="h-3 w-3 inline-flex flex-col gap-px">
          <span className="flex-1 bg-blue-400/70 rounded-t-[1px]" />
          <span className="flex-1 bg-red-400/70" />
          <span className="flex-1 bg-amber-400/70 rounded-b-[1px]" />
        </div>
      );

    // ---- GFA hazards (color match the on-map polygon fill) -------
    case 'gfa-ifr':       return <Square className="bg-fuchsia-500/30 border-fuchsia-500" />;
    case 'gfa-mtnobscn':  return <Square className="bg-stone-400/30 border-stone-400" />;
    case 'gfa-ice':       return <Square className="bg-sky-300/30 border-sky-300" />;
    case 'gfa-turblo':    return <Square className="bg-orange-500/30 border-orange-500" />;
    case 'gfa-turbhi':    return <Square className="bg-orange-700/30 border-orange-600" />;
    case 'gfa-llws':      return <Square className="bg-yellow-400/30 border-yellow-400" />;
    case 'gfa-fzlvl':     return <span className="h-3 w-3 inline-block relative"><span className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[2px] bg-cyan-300" /></span>;
    case 'gfa-sfcwnd':    return <Square className="bg-teal-500/30 border-teal-500" />;

    // ---- WeatherCams (camera silhouette) -------------------------
    case 'wxcams':
      return <CamSwatch />;

    // ---- Station-derived dot/symbol layers -----------------------
    case 'flightcat':
      // Three stacked color dots representing VFR/MVFR/IFR/LIFR.
      return (
        <div className="h-3 w-3 inline-grid grid-cols-2 grid-rows-2 gap-px">
          <span className="bg-green-500 rounded-full" />
          <span className="bg-blue-400 rounded-full" />
          <span className="bg-red-500 rounded-full" />
          <span className="bg-fuchsia-600 rounded-full" />
        </div>
      );
    case 'surfacewinds':
      return <WindBarbSwatch />;
    case 'ceilvis':
      // Mirror the on-map dot: half-filled circle (SCT) is the
      // best single-icon representative of the merged layer.
      return <CeilVisSwatch />;
    case 'temperature':
      return <ThermoSwatch />;
    case 'dewspread':
      return (
        <div className="h-3 w-3 inline-flex items-end justify-center gap-[1px]">
          <span className="w-[3px] h-2 bg-red-400 rounded-sm" />
          <span className="w-[3px] h-2 bg-cyan-300 rounded-sm" />
        </div>
      );

    case 'pireps':
      // Tiny eyeball with a colored ring — mirrors the on-map glyph
      // refreshed 2026-05-29. Ring + iris cyan (the 'icing' bucket,
      // the most common hazard class — ICE PIREPs outnumber every
      // other on a busy day) so the swatch still reads as 'cyan
      // PIREP dot' at a glance but advertises the new ring.
      return (
        <svg viewBox="0 0 14 14" className="h-3.5 w-3.5" aria-hidden>
          <circle cx="7" cy="7" r="6" fill="none" stroke="#7dd3fc" strokeWidth="1.1" />
          <path
            d="M2.5 7 Q 7 2.5 11.5 7 Q 7 11.5 2.5 7 Z"
            fill="#f8fafc" stroke="#0b0d0f" strokeWidth="0.8" strokeLinejoin="round"
          />
          <circle cx="7" cy="7" r="2.2" fill="#7dd3fc" stroke="#0b0d0f" strokeWidth="0.4" />
          <circle cx="7" cy="7" r="0.9" fill="#0b0d0f" />
        </svg>
      );

    // ---- Imagery + radar -----------------------------------------
    case 'nexrad':
      return <NexradSwatch />;
    case 'sfcanalysis':
      // 'H' + 'L' chips, blue and red, like a surface analysis map.
      return (
        <div className="h-3 w-3 inline-grid grid-cols-2 gap-[1px] items-center justify-items-center text-[8px] font-bold leading-none">
          <span className="text-red-400">L</span>
          <span className="text-blue-400">H</span>
        </div>
      );
    case 'satgeocolor':
      return <CloudSwatch tone="geocolor" />;
    case 'satir':
      return <CloudSwatch tone="ir" />;

    // ---- Ops / planning ------------------------------------------
    case 'waypoints':
      return <Dot className="bg-cyan-400" />;
    case 'fixes':
      return <FixSwatch />;
    case 'fuel100ll':
      return <FuelSwatch color="#0ea5e9" label="100" />;
    case 'fueljeta':
      return <FuelSwatch color="#facc15" label="JA" />;
    case 'terrain':
      // Red dot ringed by yellow — the TAWS color cue.
      return (
        <span className="h-3 w-3 inline-flex items-center justify-center">
          <span className="relative h-3 w-3 rounded-full bg-yellow-400 flex items-center justify-center">
            <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
          </span>
        </span>
      );
    case 'heliports':
      return <HeliSwatch />;
    case 'dispatch':
      // Lime dashed square mirrors the on-map polygon boundary.
      return <Square className="bg-lime-400/15 border-lime-300" dashed />;
    case 'ihogRings':
      // Fire Traffic Area — two concentric rings. Outer dashed amber
      // = contact, inner solid red = exclusion. Mirrors the on-map
      // FTA rendering.
      return <FtaSwatch />;
    case 'dipSites':
      // Cyan droplet — matches the dip-site recorder button + the
      // generic cyan tone used in the on-map dip-site UI. Ownership
      // color (federal green / private amber / state blue / tribal
      // purple) is per-feature on the map; the legend swatch uses
      // a neutral cyan so the toggle reads at a glance.
      return <DipSiteSwatch />;
    case 'traffic':
      // Small white aircraft silhouette — mirrors the coalt (same-
      // altitude) traffic icon used on the map.
      return <TrafficSwatch />;
    case 'obstacles':
      // Tiny tower + red beacon, mirrors the on-map obstacle symbol.
      return <ObstacleSwatch />;
    case 'repeaters':
      // Emerald antenna mast with radio waves — mirrors the on-map
      // repeater glyph.
      return <RepeaterSwatch />;
    case 'flightPlan':
      return <span className="h-1 w-3 bg-ember-400 inline-block rounded-sm" />;
    case 'ownship':
      return <Dot className="bg-ember-400" />;
    case 'smoke':
      return <Square className="bg-stone-500/30 border-stone-500" />;
    case 'hrrr-smoke':
      // Real on-map HRRR smoke layer (Phase 1–3 live as of 2026-05-26).
      // Boss req 2026-05-29: distinct icon in the layer picker so it
      // isn't a blank grey square. Mirrors the AQI gradient the layer
      // actually paints (light yellow → orange → red → brown) as a
      // rising smoke plume.
      return <SmokePlumeSwatch />;
    case 'airport-da':
      // Density-altitude airport halos. Boss req 2026-05-29: needs
      // its own icon in the layer picker. Two stacked circles using
      // the same bucket colors the layer paints on the map (sea-level
      // blue → hot-and-high red).
      return <DensityAltitudeSwatch />;

    // ---- Newer overlays (boss requested icons 2026-05-21) ---------
    case 'wind':
      return <WindParticleSwatch />;
    case 'glideRing':
      return <GlideRingSwatch />;
    case 'circlingArea':
      return <CirclingAreaSwatch />;
    case 'namedPasses':
      return <MountainPassSwatch />;
    case 'airportDiagrams':
      return <AirportDiagramSwatch />;

    default:
      return <Square className="bg-cockpit-500" />;
  }
}

// ---- Custom mini-icon components --------------------------------
function LetterChip({
  letter,
  fg,
  bg,
  border,
  dashed = false,
}: {
  letter: string;
  fg: string;
  bg: string;
  border: string;
  dashed?: boolean;
}) {
  return (
    <span
      className={[
        'h-3 w-3 inline-flex items-center justify-center border text-[8px] font-bold leading-none',
        dashed ? 'border-dashed' : '',
        bg, border, fg,
      ].join(' ')}
    >
      {letter}
    </span>
  );
}

function CamSwatch() {
  // Mini camera silhouette — same shape as the map icon.
  return (
    <svg viewBox="0 0 12 12" className="h-3 w-3" aria-hidden>
      <rect x="1" y="3.5" width="10" height="6.5" rx="1.2" fill="#bae6fd" stroke="#0ea5e9" strokeWidth="0.9" />
      <circle cx="6" cy="6.8" r="1.7" fill="#0b0d0f" stroke="#0ea5e9" strokeWidth="0.8" />
      <rect x="4" y="2.2" width="4" height="1.5" rx="0.3" fill="#0ea5e9" />
    </svg>
  );
}

function WindBarbSwatch() {
  // Tiny 15-knot barb pointing up-right.
  return (
    <svg viewBox="0 0 12 12" className="h-3 w-3" aria-hidden>
      <line x1="2" y1="10" x2="10" y2="2" stroke="#fef9c3" strokeWidth="1" strokeLinecap="round" />
      <line x1="7" y1="5" x2="10.5" y2="3" stroke="#fef9c3" strokeWidth="1" strokeLinecap="round" />
      <line x1="5.5" y1="6.5" x2="8" y2="5" stroke="#fef9c3" strokeWidth="1" strokeLinecap="round" />
    </svg>
  );
}

function ThermoSwatch() {
  return (
    <svg viewBox="0 0 12 12" className="h-3 w-3" aria-hidden>
      <rect x="4.5" y="1.5" width="3" height="7" rx="1.5" fill="#fda4af" stroke="#fb7185" strokeWidth="0.6" />
      <circle cx="6" cy="9.5" r="2" fill="#ef4444" stroke="#fb7185" strokeWidth="0.6" />
    </svg>
  );
}

function CeilVisSwatch() {
  // SCT-style half-filled circle — mirrors the on-map dot now that
  // ceiling/visibility and sky-cover share one symbol (2026-05-21).
  return (
    <svg viewBox="0 0 12 12" className="h-3 w-3" aria-hidden>
      <circle cx="6" cy="6" r="4" fill="none" stroke="#cbd5e1" strokeWidth="1" />
      <path d="M6 2 A4 4 0 0 1 6 10 L6 6 Z" fill="#cbd5e1" />
    </svg>
  );
}

function NexradSwatch() {
  // Pixellated storm cell — small grid of green/yellow/red squares.
  return (
    <div className="h-3 w-3 inline-grid grid-cols-3 grid-rows-3 gap-[0.5px]" aria-hidden>
      <span className="bg-emerald-500" />
      <span className="bg-emerald-500" />
      <span className="bg-yellow-300" />
      <span className="bg-emerald-500" />
      <span className="bg-yellow-300" />
      <span className="bg-red-500" />
      <span className="bg-yellow-300" />
      <span className="bg-red-500" />
      <span className="bg-red-500" />
    </div>
  );
}

function CloudSwatch({ tone }: { tone: 'geocolor' | 'ir' }) {
  // Stylized cloud SVG — white for geocolor on a sky background; multi-
  // color cool-to-warm gradient for IR.
  const fillId = tone === 'geocolor' ? 'cloudG' : 'cloudIR';
  const cloudFill = tone === 'geocolor' ? '#f8fafc' : `url(#${fillId})`;
  const bg = tone === 'geocolor' ? '#0ea5e9' : '#0b0d0f';
  return (
    <svg viewBox="0 0 12 12" className="h-3 w-3" aria-hidden>
      <defs>
        <linearGradient id={fillId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#22d3ee" />
          <stop offset="0.45" stopColor="#facc15" />
          <stop offset="0.75" stopColor="#f97316" />
          <stop offset="1" stopColor="#dc2626" />
        </linearGradient>
      </defs>
      <rect width="12" height="12" rx="2" fill={bg} />
      <path
        d="M3 8.2 q-1.2 0 -1.2 -1.3 q0 -1.1 1 -1.3 q0 -1.4 1.6 -1.4 q0.7 0 1.1 0.4 q0.5 -1 1.7 -1 q1.6 0 1.9 1.4 q1.2 0 1.2 1.4 q0 1.8 -2 1.8 z"
        fill={cloudFill}
        stroke="#0b0d0f"
        strokeWidth="0.4"
      />
    </svg>
  );
}

function FixSwatch() {
  return (
    <svg viewBox="0 0 12 12" className="h-3 w-3" aria-hidden>
      <polygon points="6,2 10,9.5 2,9.5" fill="#60a5fa" stroke="#1e3a8a" strokeWidth="0.8" />
    </svg>
  );
}

function ObstacleSwatch() {
  // Mirrors the redesigned on-map obstacle icon (2026-05-14): dark
  // mast on a white halo so it reads against the sectional's tan,
  // magenta flag arm for tall obstacles, red lit-beacon dot.
  return (
    <svg viewBox="0 0 12 12" className="h-3 w-3" aria-hidden>
      {/* mast */}
      <rect x="5.4" y="3.5" width="1.2" height="6.5" fill="#0b0d0f"/>
      {/* flag arm (magenta to match tall-obstacle treatment) */}
      <polygon points="6,3.5 10,4.1 6,5" fill="#e11d48" stroke="#0b0d0f" strokeWidth="0.4" strokeLinejoin="round"/>
      {/* lit beacon */}
      <circle cx="6" cy="1.9" r="1.1" fill="#ef4444" stroke="#0b0d0f" strokeWidth="0.4"/>
      {/* ground line */}
      <rect x="3.8" y="10" width="4.4" height="0.8" fill="#0b0d0f"/>
    </svg>
  );
}

function RepeaterSwatch() {
  // Mini antenna mast + radio waves — the same shape as the on-map
  // glyph but simplified for the 12x12 layer-sheet swatch slot.
  return (
    <svg viewBox="0 0 12 12" className="h-3 w-3" aria-hidden>
      {/* outer arc halos for legibility */}
      <path d="M 3.6 4 A 2.4 2.4 0 0 1 4.8 2" fill="none" stroke="#022c22" strokeWidth="1.4" strokeLinecap="round"/>
      <path d="M 8.4 4 A 2.4 2.4 0 0 0 7.2 2" fill="none" stroke="#022c22" strokeWidth="1.4" strokeLinecap="round"/>
      {/* arcs */}
      <path d="M 3.6 4 A 2.4 2.4 0 0 1 4.8 2" fill="none" stroke="#10b981" strokeWidth="0.8" strokeLinecap="round"/>
      <path d="M 8.4 4 A 2.4 2.4 0 0 0 7.2 2" fill="none" stroke="#10b981" strokeWidth="0.8" strokeLinecap="round"/>
      {/* mast halo + mast */}
      <line x1="6" y1="10" x2="6" y2="2" stroke="#022c22" strokeWidth="2.2" strokeLinecap="round"/>
      <line x1="6" y1="10" x2="6" y2="2" stroke="#10b981" strokeWidth="1.1" strokeLinecap="round"/>
      {/* ground line */}
      <rect x="4.2" y="9.6" width="3.6" height="0.9" fill="#022c22" rx="0.3"/>
    </svg>
  );
}

function HeliSwatch() {
  // Magenta H on a circular badge — mirrors the on-map heliport symbol.
  // CSS approach (more reliable than tiny SVG text at this size).
  return (
    <span
      className="h-3 w-3 inline-flex items-center justify-center rounded-full border-[1.2px] border-fuchsia-500 text-fuchsia-500 text-[8px] font-bold leading-none"
    >
      H
    </span>
  );
}

function FtaSwatch() {
  // Two concentric rings: outer dashed amber (contact, 12 NM), inner
  // solid red (exclusion, 7 NM). Faithful to the on-map FTA paint.
  return (
    <svg viewBox="0 0 12 12" className="h-3 w-3" aria-hidden>
      <circle cx="6" cy="6" r="4.8" fill="none" stroke="#f59e0b" strokeWidth="0.9" strokeDasharray="1.8 1.2" />
      <circle cx="6" cy="6" r="2.4" fill="#dc2626" fillOpacity="0.18" stroke="#dc2626" strokeWidth="0.9" />
    </svg>
  );
}

function DipSiteSwatch() {
  // Cyan droplet — generic water-source glyph. Outline + small inner
  // highlight to read at the 12×12 swatch size.
  return (
    <svg viewBox="0 0 12 12" className="h-3 w-3" aria-hidden>
      <path
        d="M6 1.4 C 8.3 4.4, 9.7 6.1, 9.7 8.0 A 3.7 3.7 0 0 1 2.3 8.0 C 2.3 6.1, 3.7 4.4, 6 1.4 Z"
        fill="#22d3ee"
        stroke="#0e7490"
        strokeWidth="0.7"
      />
      <ellipse cx="4.8" cy="7" rx="0.7" ry="1.0" fill="#ecfeff" opacity="0.85" />
    </svg>
  );
}

function TrafficSwatch() {
  // Tiny aircraft silhouette — same shape as the on-map coalt
  // (same-altitude) traffic icon. Pointed nose-up for the legend.
  return (
    <svg viewBox="0 0 12 12" className="h-3 w-3" aria-hidden>
      <path
        d="M6 1.2 L 6.55 4.6 L 10.6 6.6 L 10.6 7.2 L 6.55 6.4 L 6.4 9.2 L 7.7 9.9 L 7.7 10.5 L 6 10.0 L 4.3 10.5 L 4.3 9.9 L 5.6 9.2 L 5.45 6.4 L 1.4 7.2 L 1.4 6.6 L 5.45 4.6 Z"
        fill="#f3f4f6"
        stroke="#0b0d0f"
        strokeWidth="0.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** HRRR smoke layer — stylized rising plume in the AQI color ramp
 *  (yellow→orange→red→brown). Mirrors the colors the layer actually
 *  paints on the map at increasing PM2.5 concentrations. */
function SmokePlumeSwatch() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" aria-hidden>
      <defs>
        <linearGradient id="smokeGrad" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0"   stopColor="#78350f" />{/* brown base */}
          <stop offset="0.4" stopColor="#ef4444" />{/* red */}
          <stop offset="0.7" stopColor="#f97316" />{/* orange */}
          <stop offset="1"   stopColor="#facc15" />{/* yellow top */}
        </linearGradient>
      </defs>
      {/* fire base */}
      <rect x="5" y="13" width="6" height="1.5" fill="#78350f" rx="0.3" />
      {/* rising plume — a wavy column that widens slightly toward the top */}
      <path
        d="M6 13 C 5.2 10 8.8 9 7.5 6.5 C 6.5 4.5 9.5 4 9 1.5 L 11 1.5 C 11.5 4 8.5 4.5 9.5 6.5 C 10.8 9 7.2 10 8 13 Z"
        fill="url(#smokeGrad)"
        stroke="#0b0d0f"
        strokeWidth="0.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Density-altitude airport halos — two concentric circles using the
 *  layer's bucket palette (sea-level blue inner, hot+high red outer)
 *  with "DA" letters. Reads as "airport DA chip" at swatch size. */
function DensityAltitudeSwatch() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" aria-hidden>
      {/* hot+high red outer halo */}
      <circle cx="8" cy="8" r="7" fill="#ef4444" fillOpacity="0.35" stroke="#ef4444" strokeWidth="1" />
      {/* attention yellow mid band */}
      <circle cx="8" cy="8" r="5" fill="#eab308" fillOpacity="0.55" stroke="#eab308" strokeWidth="0.6" />
      {/* normal green inner */}
      <circle cx="8" cy="8" r="3" fill="#22c55e" stroke="#0b0d0f" strokeWidth="0.6" />
      <text x="8" y="10" textAnchor="middle" fontSize="3.6" fontWeight="700" fill="#0b0d0f" fontFamily="system-ui, sans-serif">DA</text>
    </svg>
  );
}

/** Wind layer — three small white-to-orange streaks suggesting flowing
 *  particles. Mirrors the on-map white→orange particle gradient. */
function WindParticleSwatch() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5">
      <path d="M2 5 H8"  stroke="#ffffff" strokeWidth="1.5" strokeLinecap="round" opacity="0.95"/>
      <path d="M3 9 H11" stroke="#ffd28a" strokeWidth="1.5" strokeLinecap="round" opacity="0.95"/>
      <path d="M2 13 H7" stroke="#ff8c1a" strokeWidth="1.5" strokeLinecap="round" opacity="0.95"/>
      <circle cx="8" cy="5"  r="1.4" fill="#ffffff"/>
      <circle cx="11" cy="9" r="1.4" fill="#ffd28a"/>
      <circle cx="7" cy="13" r="1.4" fill="#ff8c1a"/>
    </svg>
  );
}

/** Glide ring — green circle outline. Matches the on-map ring color. */
function GlideRingSwatch() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5">
      <circle cx="8" cy="8" r="6" fill="none" stroke="#22c55e" strokeWidth="1.6"/>
      <circle cx="8" cy="8" r="1.3" fill="#22c55e"/>
    </svg>
  );
}

/** Circling area — dashed cyan oval representing the TERPS circling
 *  protection area drawn around a runway. */
function CirclingAreaSwatch() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5">
      <ellipse cx="8" cy="8" rx="6" ry="4.2" fill="#22d3ee22"
               stroke="#22d3ee" strokeWidth="1.4" strokeDasharray="2 1.6"/>
      <rect x="6" y="7.3" width="4" height="1.4" fill="#94a3b8" rx="0.3"/>
    </svg>
  );
}

/** Mountain pass — two peaks with a saddle gap. Mirrors the on-map
 *  named-pass glyph. */
function MountainPassSwatch() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5">
      <path d="M1 13 L5 5 L8 9 L11 4 L15 13 Z"
            fill="#a8a29e" stroke="#57534e" strokeWidth="0.7" strokeLinejoin="round"/>
      <path d="M5 5 L6.5 6.8 L8 9" fill="none" stroke="#fafaf9" strokeWidth="0.6"/>
      <path d="M11 4 L12.5 6.5 L15 13" fill="none" stroke="#fafaf9" strokeWidth="0.6"/>
    </svg>
  );
}

/** Airport diagram — small crossed-runway plan view. */
function AirportDiagramSwatch() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5">
      <rect x="1" y="7" width="14" height="2"
            fill="#374151" stroke="#9ca3af" strokeWidth="0.6"/>
      <rect x="7" y="1" width="2" height="14"
            fill="#374151" stroke="#9ca3af" strokeWidth="0.6"
            transform="rotate(35 8 8)"/>
    </svg>
  );
}

function FuelSwatch({ color, label }: { color: string; label: string }) {
  // Tiny fuel-can: a small color chip with a 2-letter abbreviation
  // (100 for 100LL, JA for Jet-A). Reads better than a plain dot at
  // the LayerSheet's icon size.
  return (
    <span
      className="h-3 w-3 inline-flex items-center justify-center rounded-sm text-[7px] font-bold leading-none text-cockpit-900"
      style={{ background: color }}
    >
      {label}
    </span>
  );
}

function Square({ className = '', dashed = false }: { className?: string; dashed?: boolean }) {
  return (
    <span
      className={`h-3 w-3 inline-block border ${dashed ? 'border-dashed' : ''} ${className}`}
    />
  );
}

function Dot({ className = '' }: { className?: string }) {
  return <span className={`h-2 w-2 rounded-full inline-block ${className}`} />;
}

function Hatched({
  className = '',
  hatching,
}: { className?: string; hatching: 'red' | 'orange' | 'purple' | 'yellow' }) {
  return (
    <span className={`relative h-3 w-3 inline-block border ${className} overflow-hidden`}>
      <span
        className="absolute inset-0"
        style={{
          backgroundImage: `repeating-linear-gradient(45deg, ${hatchColor(hatching)} 0 1.5px, transparent 1.5px 4px)`,
        }}
      />
    </span>
  );
}

/** ForeFlight-style radar variant selector — rendered as a 3-button segmented
 *  control under the NEXRAD layer row when the layer is on. Lets the boss pick
 *  between the three NOAA radar products without cluttering the catalog with
 *  three separate mutually-exclusive layer rows. (2026-05-21 19:42 PDT.) */
function RadarVariantPicker() {
  const variant = useUiStore((s) => s.nexradVariant);
  const setVariant = useUiStore((s) => s.setNexradVariant);
  const options: Array<{
    id: 'composite' | 'classic' | 'lowtilt';
    label: string;
    hint: string;
  }> = [
    { id: 'composite', label: 'Radar',         hint: 'MRMS Composite Reflectivity (modern, multi-sensor)' },
    { id: 'classic',   label: 'Classic',       hint: 'NEXRAD Level III base reflectivity composite' },
    { id: 'lowtilt',   label: 'Low Tilt',      hint: 'MRMS Seamless Hybrid-Scan — closest-to-ground beam' },
  ];
  return (
    <div className="ml-7 mr-1 mb-1 -mt-0.5 flex gap-1">
      {options.map((o) => {
        const active = variant === o.id;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => setVariant(o.id)}
            title={o.hint}
            className={[
              'flex-1 text-[10px] py-1 rounded border transition-colors',
              active
                ? 'bg-cyan-500/20 border-cyan-400 text-cyan-100'
                : 'bg-cockpit-700/40 border-cockpit-600 text-cockpit-300 hover:bg-cockpit-700/70',
            ].join(' ')}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function Switch({ checked }: { checked: boolean }) {
  return (
    <span
      className={[
        'inline-block h-4 w-7 rounded-full relative transition-colors shrink-0',
        checked ? 'bg-ember-500' : 'bg-cockpit-500',
      ].join(' ')}
    >
      <span
        className={[
          'absolute top-0.5 h-3 w-3 rounded-full bg-white transition-transform',
          checked ? 'translate-x-3.5' : 'translate-x-0.5',
        ].join(' ')}
      />
    </span>
  );
}

function hatchColor(c: 'red' | 'orange' | 'purple' | 'yellow') {
  switch (c) {
    case 'red':    return 'rgba(220,38,38,0.95)';
    case 'orange': return 'rgba(234,88,12,0.95)';
    case 'purple': return 'rgba(124,58,237,0.95)';
    case 'yellow': return 'rgba(202,138,4,0.95)';
  }
}

function SuaLegend() {
  const items: Array<{ label: string; color: string; hatching: 'red' | 'orange' | 'purple' | 'yellow' }> = [
    { label: 'Prohibited / TFR', color: 'rgba(220,38,38,0.25)', hatching: 'red' },
    { label: 'Restricted',       color: 'rgba(234,88,12,0.25)', hatching: 'orange' },
    { label: 'MOA',              color: 'rgba(124,58,237,0.25)', hatching: 'purple' },
    { label: 'Warning / Alert',  color: 'rgba(202,138,4,0.20)', hatching: 'yellow' },
  ];
  return (
    <div>
      <div className="text-[10px] tracking-widest text-cockpit-300 mb-1">SUA LEGEND</div>
      <ul className="grid grid-cols-2 gap-x-2 gap-y-1">
        {items.map((it) => (
          <li key={it.label} className="flex items-center gap-1.5 text-[10px] text-cockpit-200">
            <span
              className="h-3 w-4 inline-block border border-cockpit-500"
              style={{
                backgroundColor: it.color,
                backgroundImage: `repeating-linear-gradient(45deg, ${hatchColor(it.hatching)} 0 1.5px, transparent 1.5px 4px)`,
              }}
            />
            {it.label}
          </li>
        ))}
      </ul>
    </div>
  );
}
